import React from "react";
import { Button } from "@/components/new/button";
import { Label } from "@/components/new/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import type { FieldErrors, UseFormSetValue } from "react-hook-form";
import type { TProductReviewForm } from "@/types/productv2";

type Props = {
	startDate: string | null | undefined;
	endDate: string | null | undefined;
	isOngoing: boolean;
	setIsOngoing: (val: boolean) => void;
	setStartDateOpen: (open: boolean) => void;
	setEndDateOpen: (open: boolean) => void;
	setValue: UseFormSetValue<TProductReviewForm>;
	errors: FieldErrors<TProductReviewForm>;
};

export const UsageTimelineSection = ({
	startDate,
	endDate,
	isOngoing,
	setIsOngoing,
	setStartDateOpen,
	setEndDateOpen,
	setValue,
	errors,
}: Props) => {
	const startDateObj = startDate ? new Date(startDate) : undefined;
	const endDateObj = endDate ? new Date(endDate) : undefined;

	return (
		<div className="space-y-3">
			<Label className="text-xl font-semibold">
				Usage Timeline <span className="text-red-500">*</span>
			</Label>

			<div className="flex flex-col gap-4 md:flex-row md:items-center">
				{/* Start Date Picker */}
				<div className="w-full md:w-[220px]">
					<Label>
						Start Date <span className="text-red-500">*</span>
					</Label>
					<Popover onOpenChange={setStartDateOpen}>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"h-10 w-full justify-start rounded-lg border border-[#7777] bg-white px-4 text-left text-sm font-normal",
									!startDate && "text-gray-400"
								)}
							>
								<CalendarIcon className="mr-2 h-4 w-4 text-gray-600" />
								{startDate ? format(startDateObj!, "PPP") : "Pick a start date"}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0">
							<Calendar
								mode="single"
								selected={startDateObj}
								onSelect={(date) => {
									if (date) {
										setValue("startDate", date.toISOString(), {
											shouldDirty: true,
											shouldValidate: true,
										});
										setTimeout(() => setStartDateOpen(false), 100);
									}
								}}
								initialFocus
							/>
						</PopoverContent>
					</Popover>
					{errors.startDate && (
						<p className="mt-1 text-xs text-red-500">{errors.startDate.message}</p>
					)}
				</div>

				{/* Switch */}
				<div className="flex items-center space-x-3 md:mt-6">
					<Label htmlFor="ongoing" className="whitespace-nowrap text-sm">
						Currently in use
					</Label>
					<Switch
						id="ongoing"
						checked={isOngoing}
						onCheckedChange={(checked) => {
							setIsOngoing(checked);
							if (checked) {
								setValue("endDate", undefined, {
									shouldDirty: true,
									shouldValidate: true,
								});
								setEndDateOpen(false);
							}
						}}
					/>
				</div>

				{/* End Date Picker (only when not ongoing) */}
				{!isOngoing && (
					<div className="w-full md:w-[220px]">
						<Label>
							End Date <span className="text-red-500">*</span>
						</Label>
						<Popover onOpenChange={setEndDateOpen}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className={cn(
										"h-10 w-full justify-start rounded-lg border border-[#7777] bg-white px-4 text-left text-sm font-normal",
										!endDate && "text-gray-400"
									)}
								>
									<CalendarIcon className="mr-2 h-4 w-4 text-gray-600" />
									{endDate ? format(endDateObj!, "PPP") : "Pick an end date"}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0">
								<Calendar
									mode="single"
									selected={endDateObj}
									onSelect={(date) => {
										if (date) {
											setValue("endDate", date.toISOString(), {
												shouldValidate: true,
												shouldDirty: true,
											});
											setTimeout(() => setEndDateOpen(false), 100);
										}
									}}
									initialFocus
								/>
							</PopoverContent>
						</Popover>
						{errors.endDate && (
							<p className="mt-1 text-xs text-red-500">{errors.endDate.message}</p>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
